import * as fs from "node:fs";
import { parse as parseYaml } from "yaml";
import { LocalCliError } from "./errors.js";
import { serializeA2aCard, A2A_CARD_MAX_CHARS, type A2aCard } from "@m8t-stack/api-contract";

/** Read the a2a-card (targets.foundry.a2a-card) + Layer-1 role from a persona
 *  file and produce the metadata-ready A2aCard. Throws if the card block is
 *  missing or the serialized card exceeds the Foundry metadata value cap. */
export function readPersonaA2aCard(personaPath: string): { card: A2aCard; serialized: string } {
  let raw: string;
  try {
    raw = fs.readFileSync(personaPath, "utf8");
  } catch (e) {
    throw new LocalCliError({ code: "PERSONA_READ_FAILED", message: `Could not read persona '${personaPath}': ${(e as Error).message}` });
  }
  // \r?\n tolerates CRLF line endings (Windows git checkouts) as well as LF.
  const fm = /^---\r?\n([\s\S]*?)\r?\n---/.exec(raw);
  if (!fm) throw new LocalCliError({ code: "PERSONA_NO_FRONTMATTER", message: `Persona '${personaPath}' has no frontmatter.` });

  const front = parseYaml(fm[1]) as Record<string, unknown>;
  const role = typeof front.role === "string" ? front.role : "";
  const cardYaml = (front.targets as { foundry?: { ["a2a-card"]?: Record<string, unknown> } } | undefined)?.foundry?.["a2a-card"];
  if (!cardYaml || typeof cardYaml !== "object") {
    throw new LocalCliError({
      code: "A2A_CARD_MISSING",
      message: `Persona '${personaPath}' opts into a2a but has no targets.foundry.a2a-card block.`,
      hint: "Add a2a-card with summary / when-to-delegate / accepts / returns.",
    });
  }
  const card: A2aCard = {
    role,
    summary: str(cardYaml.summary),
    whenToDelegate: str(cardYaml["when-to-delegate"]),
    accepts: str(cardYaml.accepts),
    returns: str(cardYaml.returns),
  };
  const serialized = serializeA2aCard(card);
  if (serialized.length > A2A_CARD_MAX_CHARS) {
    throw new LocalCliError({
      code: "A2A_CARD_TOO_LONG",
      message: `a2a-card for '${personaPath}' serializes to ${serialized.length.toString()} chars (max ${A2A_CARD_MAX_CHARS.toString()}). Shorten summary / when-to-delegate / accepts / returns.`,
    });
  }
  return { card, serialized };
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}
